import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPIAdmin } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';

export const addFleet = createAsyncThunk(
  'fleetAdmin/add',
  async (fleetData, { rejectWithValue }) => {
    try {
      const response = await callAPIAdmin(apiUrls.adminCreateFleet, {}, 'POST', fleetData);
      if (response.data.success) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to add vehicle');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

export const updateFleet = createAsyncThunk(
  'fleetAdmin/update',
  async ({ id, fleetData }, { rejectWithValue }) => {
    try {
      const response = await callAPIAdmin(`${apiUrls.adminUpdateFleet}/${id}`, {}, 'PATCH', fleetData);
      if (response.data.success) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to update vehicle');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

export const deleteFleet = createAsyncThunk(
  'fleetAdmin/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await callAPIAdmin(apiUrls.adminDeleteFleet, { _id: id }, 'DELETE', {});
      if (response.data.success) {
        return { id, message: response.data.message };
      }
      return rejectWithValue(response.data.message || 'Failed to delete vehicle');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

export const updateFleetStatus = createAsyncThunk(
  'fleetAdmin/updateStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await callAPIAdmin(apiUrls.adminFleetStatusUpdate, { _id: id, status }, 'PATCH', {});
      if (response.data.success) {
        return { id, status, message: response.data.message };
      }
      return rejectWithValue(response.data.message || 'Failed to update status');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

const initialState = {
  mutationLoading: false,
  mutationError: null,
  mutationSuccess: null,
};

const fleetAdminSlice = createSlice({
  name: 'fleetAdmin',
  initialState,
  reducers: {
    clearMutationError: (state) => {
      state.mutationError = null;
    },
    clearMutationSuccess: (state) => {
      state.mutationSuccess = null;
    },
    resetMutationState: (state) => {
      state.mutationLoading = false;
      state.mutationError = null;
      state.mutationSuccess = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addFleet.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.mutationSuccess = null;
      })
      .addCase(addFleet.fulfilled, (state) => {
        state.mutationLoading = false;
        state.mutationSuccess = 'Vehicle added successfully';
      })
      .addCase(addFleet.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload;
      })
      .addCase(updateFleet.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.mutationSuccess = null;
      })
      .addCase(updateFleet.fulfilled, (state) => {
        state.mutationLoading = false;
        state.mutationSuccess = 'Vehicle updated successfully';
      })
      .addCase(updateFleet.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload;
      })
      .addCase(deleteFleet.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.mutationSuccess = null;
      })
      .addCase(deleteFleet.fulfilled, (state) => {
        state.mutationLoading = false;
        state.mutationSuccess = 'Vehicle deleted successfully';
      })
      .addCase(deleteFleet.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload;
      })
      .addCase(updateFleetStatus.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.mutationSuccess = null;
      })
      .addCase(updateFleetStatus.fulfilled, (state, action) => {
        state.mutationLoading = false;
        state.mutationSuccess = `Status changed to ${action.payload?.status}`;
      })
      .addCase(updateFleetStatus.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload;
      });
  },
});

export const { clearMutationError, clearMutationSuccess, resetMutationState } = fleetAdminSlice.actions;
export default fleetAdminSlice.reducer;